import React from 'react';
import { X } from 'lucide-react';
import { useDiscardGuard } from '../hooks/useDiscardGuard';

/** Kopfzeile wie in den übrigen AppModal-Dialogen: Titel links, ✕ rechts. */
export function AppModalHeader({
  title,
  titleId,
  onClose,
  closeDisabled = false,
}: {
  title: React.ReactNode;
  titleId?: string;
  onClose: () => void;
  closeDisabled?: boolean;
}) {
  return (
    <div className="flex shrink-0 items-center justify-between gap-3 border-b border-app-border px-5 py-4">
      <h2 id={titleId} className="min-w-0 truncate text-base font-semibold text-app-text">
        {title}
      </h2>
      <button
        type="button"
        onClick={onClose}
        disabled={closeDisabled}
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-app-text-subtle hover:bg-app-hover disabled:opacity-40"
        aria-label="Schließen"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

type AppModalProps = {
  open: boolean;
  onClose: () => void;
  /**
   * Content, or a function that gets `requestClose` — wire the ✕ button to it so it asks
   * before discarding, like Esc and the backdrop do.
   */
  children: React.ReactNode | ((requestClose: () => void) => React.ReactNode);
  overlayClassName?: string;
  panelClassName?: string;
  disableClose?: boolean;
  closeOnOverlayClick?: boolean;
  ariaLabelledBy?: string;
  /** Unsaved input: Esc, backdrop and `requestClose` ask „Entwurf verwerfen?“ first. */
  dirty?: boolean;
  discardDescription?: string;
};

export function AppModal({
  open,
  onClose,
  children,
  overlayClassName = 'z-40',
  panelClassName = 'md:max-w-3xl',
  disableClose = false,
  closeOnOverlayClick = true,
  ariaLabelledBy,
  dirty = false,
  discardDescription = 'Deine Eingaben sind noch nicht gespeichert und gehen verloren.',
}: AppModalProps) {
  const overlayMouseDownRef = React.useRef(false);
  const guard = useDiscardGuard({
    dirty,
    onClose,
    disabled: disableClose,
    description: discardDescription,
  });
  const requestCloseRef = React.useRef(guard.requestClose);
  requestCloseRef.current = guard.requestClose;

  React.useEffect(() => {
    if (!open) return;
    const resetOverlayPress = () => {
      overlayMouseDownRef.current = false;
    };
    document.addEventListener('mouseup', resetOverlayPress);
    return () => document.removeEventListener('mouseup', resetOverlayPress);
  }, [open]);

  React.useEffect(() => {
    if (!open || disableClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('[data-app-select-list]')) return;
      e.preventDefault();
      e.stopPropagation();
      // With the discard question open, Esc dismisses the question (see useDiscardGuard).
      requestCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, disableClose]);

  if (!open) return null;

  return (
    <>
      <div
        className={`fixed inset-0 ${overlayClassName} flex flex-col bg-app-surface md:items-center md:justify-center md:bg-black/40 md:p-4`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabelledBy}
        onMouseDown={(e) => {
          overlayMouseDownRef.current = e.target === e.currentTarget;
        }}
        onMouseUp={(e) => {
          if (disableClose || !closeOnOverlayClick) return;
          if (e.target === e.currentTarget && overlayMouseDownRef.current) guard.requestClose();
        }}
      >
        <div
          className={`flex flex-col w-full h-full min-h-0 md:h-auto md:bg-app-surface md:rounded-xl md:shadow-2xl md:max-h-[90vh] md:border md:border-app-border ${panelClassName}`}
          onClick={(e) => e.stopPropagation()}
        >
          {typeof children === 'function' ? children(guard.requestClose) : children}
        </div>
      </div>
      {guard.dialog}
    </>
  );
}
